import React, { useCallback, useId } from 'react';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import * as screensizes from '@loomstateorg/shared-frontend/utils/screensizes';

export type DividerType = 'none' | 'solid' | 'dashed' | 'dotted' | 'double';

const HEX_COLOR_PATTERN = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const normalizeColor = (color?: string): string | undefined => {
  const match = color && HEX_COLOR_PATTERN.exec(color.trim());
  return match ? `#${match[1]}` : undefined;
};

export interface Props {
  children?: React.ReactNode;
  backgroundColor: 'white' | 'neutral-200';
  customMobile: number;
  customTablet: number;
  customDesktop: number;
  dividerType?: DividerType;
  dividerThickness?: number;
  dividerColor?: string;
  dividerWidth?: number;
  customCss?: string;
}

const Spacer: React.FC<Props> = ({
  customMobile,
  customTablet,
  customDesktop,
  backgroundColor = 'white',
  dividerType = 'none',
  dividerThickness = 1,
  dividerColor: dividerColorProp,
  dividerWidth = 100,
  customCss,
}) => {
  const dividerColor = normalizeColor(dividerColorProp) ?? 'currentColor';
  const [isTablet] = useMediaQuery(screensizes.tablet);
  const [isDesktop] = useMediaQuery(screensizes.desktop);
  const reactId = useId();
  const uniqueClass = `spacer-${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;

  const getSpacing = useCallback(() => {
    if (isDesktop) return customDesktop;
    if (isTablet) return customTablet;
    return customMobile;
  }, [isTablet, isDesktop, customMobile, customTablet, customDesktop]);

  const hasDivider = dividerType !== 'none';

  return (
    <div
      className={`${uniqueClass} relative flex w-full items-center justify-center bg-${backgroundColor}`}
      style={{ height: `${getSpacing() || 0}px` }}
      data-testid="spacer-container"
    >
      {hasDivider && (
        <hr
          aria-hidden="true"
          data-testid="spacer-divider-line"
          className="m-0"
          style={{
            width: `${dividerWidth}%`,
            borderTopStyle: dividerType,
            borderTopWidth: `${dividerThickness}px`,
            borderTopColor: dividerColor,
          }}
        />
      )}
      {customCss && (
        // eslint-disable-next-line react/no-danger
        <style data-testid="spacer-custom-css" dangerouslySetInnerHTML={{ __html: `.${uniqueClass} { ${customCss} }` }} />
      )}
    </div>
  );
};

export default Spacer;
